/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@monsma/XDL/Helpers";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    copy = "copy",
    buttonLabel = "buttonLabel",
    buttonURL = "buttonURL",
    firstContent = "firstContent",
    secondContent = "secondContent",
    thirdContent = "thirdContent",
    forthContent = "forthContent",
}

interface CompanyProfileProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.copy]: string;
        [fields.buttonLabel]: string;
        [fields.buttonURL]: LinkFieldValue;
        [fields.firstContent]: string;
        [fields.secondContent]: string;
        [fields.thirdContent]: string;
        [fields.forthContent]: string;
    };
}

interface CompanyProfileStyles {
    wrapper?: InjectableCss;
    firstSection?: InjectableCss;
    titleWrapper?: InjectableCss;
    copyWrapper?: InjectableCss;
    secondSection?: InjectableCss;
    contentWrapper?: InjectableCss;
}

const styles: CompanyProfileStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${screen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    firstSection: {
        css: css`
            flex: 1;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 20px;
            ${TypographyStyle as any} {
                font-size: 28px;
                font-weight: bold;
                line-height: 34px;
                margin-bottom: 0;
            }
        `,
    },
    copyWrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    secondSection: {
        css: css`
            flex: 0 0 300px;
            padding: 15px 0 20px 45px;
            margin-left: 65px;
            border-left: 1px solid ${getColor("common.ui12")};
            ${screen("lte", "md")} {
                flex: 1;
                padding: 60px 0 0;
                margin-left: 0;
                margin-top: 60px;
                border-left-width: 0;
                border-top: 1px solid ${getColor("common.ui12")};
            }
        `,
    },
    contentWrapper: {
        css: css`
            margin-bottom: 38px;
            &:last-child {
                margin-bottom: 0;
            }
            ${TypographyStyle as any} {
                font-size: 16px;
                font-weight: bold;
                line-height: 20px;
                margin-bottom: 0;
            }
        `,
    },
};

const CompanyProfile: React.FC<CompanyProfileProps> = ({ fields }) => {
    const { title, copy, buttonLabel, firstContent, secondContent, thirdContent, forthContent } = fields;
    const buttonURL = useGetLink(fields.buttonURL);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.firstSection}>
                <StyledWrapper {...styles.titleWrapper}>{title && parse(title, parserOptions)}</StyledWrapper>
                <StyledWrapper {...styles.copyWrapper}>{copy && parse(copy, parserOptions)}</StyledWrapper>
                <Button forwardAs="a" {...({ href: buttonURL.url } as any)}>
                    {buttonLabel}
                </Button>
            </StyledWrapper>
            {(firstContent || secondContent || thirdContent || forthContent) && (
                <StyledWrapper {...styles.secondSection}>
                    {firstContent && (
                        <StyledWrapper {...styles.contentWrapper}>{parse(firstContent, parserOptions)}</StyledWrapper>
                    )}
                    {secondContent && (
                        <StyledWrapper {...styles.contentWrapper}>{parse(secondContent, parserOptions)}</StyledWrapper>
                    )}
                    {thirdContent && (
                        <StyledWrapper {...styles.contentWrapper}>{parse(thirdContent, parserOptions)}</StyledWrapper>
                    )}
                    {forthContent && (
                        <StyledWrapper {...styles.contentWrapper}>{parse(forthContent, parserOptions)}</StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const collapsedToolbarButtons = {
    moreText: {
        buttons: ["textColor"],
    },
};

const expandedToolbarButtons = {
    moreParagraph: {},
    moreText: {
        buttons: ["textColor"],
    },
    moreMisc: {
        buttons: ["insertLink"],
    },
    code: {},
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            isRequired: true,
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
        {
            name: fields.copy,
            displayName: "Copy",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            isRequired: true,
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
        {
            name: fields.buttonLabel,
            displayName: "Button Label",
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            name: fields.buttonURL,
            displayName: "Button URL",
            editorTemplate: "LinkField",
            defaultValue: { type: "Page", value: "" },
            fieldType: "Contextual",
            isRequired: true,
        },
        {
            name: fields.firstContent,
            displayName: "First Content",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            extendedConfig: {
                height: 170,
            },
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
        {
            name: fields.secondContent,
            displayName: "Second Content",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            extendedConfig: {
                height: 170,
            },
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
        {
            name: fields.thirdContent,
            displayName: "Third Content",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            extendedConfig: {
                height: 170,
            },
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
        {
            name: fields.forthContent,
            displayName: "Forth Content",
            fieldType: "General",
            editorTemplate: "RichTextField",
            defaultValue: "",
            extendedConfig: {
                height: 170,
            },
            collapsedToolbarButtons,
            expandedToolbarButtons,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: CompanyProfile,
    definition,
};

export default widgetModule;
